import { useLazyQuery } from '@apollo/client';
import { FIND_LIST_VALUES_OPTIMIZED } from 'graphql/queries/customLists';
import { FindListValuesOptimized } from 'ui-component/formComponents/LazyLoadingList/types';
import { useEffect, useMemo, useState } from 'react';
import { IRecordFieldsList, IRecordHeaders } from 'ui-component/records/types';
import { getListValueIdsFromData } from 'ui-component/records/utils/headerHelpers';
import { FindListValuesVariables, ListValue } from 'views/backoffice/CustomLists/types';
import { orderListValuesById } from 'views/backoffice/CustomLists/utils';

export const useSelectedAndDefaultListTypesValues = (
    headers?: IRecordFieldsList,
    list?: IRecordHeaders[] | IRecordHeaders,
    onCompleted?: () => void
) => {
    const [isLoading, setIsLoading] = useState(true);
    const [fetchOptimized] = useLazyQuery<FindListValuesOptimized, FindListValuesVariables>(FIND_LIST_VALUES_OPTIMIZED);
    const [listData, setListData] = useState<ListValue[]>([]);

    const valuesIds = useMemo(() => {
        if (!list) return [];
        const formattedList = Array.isArray(list) ? list : [list];
        return [...new Set(getListValueIdsFromData(headers, formattedList).filter((id) => !isNaN(Number(id))))];
    }, [headers, list]);

    const listIds = useMemo(
        () => [
            ...new Set(
                Object.entries(headers || {})
                    ?.filter(([_, value]) => ['multiselect', 'multiselect-checkbox'].includes(value?.dataType) && value?.listType?.id)
                    ?.map(([_, value]) => Number(value?.listType?.id))
            )
        ],
        [headers]
    );

    const listValuesByListId = useMemo(() => (listData?.length ? orderListValuesById(listData) : []), [listData]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                if (listIds?.length) {
                    const { data } = await fetchOptimized({
                        variables: { data: { listIds } }
                    });
                    setListData((curData) => [...(data?.findListValuesOptimized || [])]);
                }
                if (valuesIds?.length) {
                    const { data } = await fetchOptimized({ variables: { data: { ids: valuesIds } } });
                    // this fetch can have duplicates, so we have to remove
                    setListData((curData) => [
                        ...new Map([...curData, ...(data?.findListValuesOptimized || [])].map((val) => [val?.id, val]))?.values()
                    ]);
                }
                onCompleted?.();
            } catch (error) {
                console.log(error);
            }
            setIsLoading(false);
        };
        if (listIds?.length || valuesIds?.length) {
            fetchData();
        } else setIsLoading(false);
    }, [fetchOptimized, listIds, valuesIds, onCompleted]);

    return { isLoading, listValuesByListId };
};
